import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PublishIcon from '@mui/icons-material/Publish';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import SaveIcon from '@mui/icons-material/Save';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useAuth } from '@frontend/contexts/AuthContext';
import type {
  CliScreenExampleFidelity,
  CliScreenExampleInput,
  CliScreenExampleRecord,
} from '@modules/shared-types';
import {
  StyledSelectDropdown,
  StyledTextArea,
  StyledTextField,
} from '@modules/ui-components';
import { PROMPT_SECTIONS_ADMIN } from '@test-kit/selectors/prompt-sections-admin';
import {
  BLOCK_SCREEN_EXAMPLE_PACKS_PATH,
  createPromptSectionsAdminApiService,
  type PromptRegistryVersionView,
  type PromptRegistryVersionsData,
} from '../services/PromptSectionsAdminApiService';
import { BlockSubtypePromotionPanel } from '../components/BlockSubtypePromotionPanel';

const DEFAULT_ACCEPTANCE_PROJECT_ID = 'FINo7LxKWBPXHochQK0n';
const FIDELITY_OPTIONS = [
  { value: 'strict-template', label: 'Strict template' },
  { value: 'pattern', label: 'Pattern' },
  { value: 'inspiration', label: 'Inspiration' },
];

interface GlobalExamplePack {
  id: string;
  fidelity?: CliScreenExampleFidelity;
  appliesWhen: {
    agentId: 'blockScreen';
    blockTypes?: string[];
    screenTypes?: string[];
    [key: string]: unknown;
  };
  teaches: string[];
  outputPattern: Record<string, unknown>;
  forbiddenCopyBehavior: string[];
  [key: string]: unknown;
}

interface GlobalExamplePackSet {
  version: string;
  examples: GlobalExamplePack[];
}

interface ProjectExampleDraft {
  id: string;
  name: string;
  screenTypes: string;
  blockTypes: string;
  fidelity: CliScreenExampleFidelity;
  bodyJson: string;
  enabled: boolean;
  persisted: boolean;
}

const EMPTY_PROJECT_DRAFT: ProjectExampleDraft = {
  id: '',
  name: '',
  screenTypes: '',
  blockTypes: '',
  fidelity: 'pattern',
  bodyJson: JSON.stringify({
    teaches: [],
    outputPattern: { screens: [] },
    forbiddenCopyBehavior: [],
  }, null, 2),
  enabled: true,
  persisted: false,
};

export function AdminPromptSectionsPage(): React.ReactElement {
  const { adminLoaded, isAdmin } = useAuth();
  const api = useMemo(() => createPromptSectionsAdminApiService(), []);
  const [tab, setTab] = useState<'global' | 'project' | 'promotion'>('global');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const [globalJson, setGlobalJson] = useState('');
  const [selectedPackId, setSelectedPackId] = useState('');
  const [packJson, setPackJson] = useState('');
  const [search, setSearch] = useState('');
  const [versions, setVersions] = useState<PromptRegistryVersionsData | null>(null);
  const [validatedSnapshot, setValidatedSnapshot] = useState<string | null>(null);
  const [validatedChecksum, setValidatedChecksum] = useState<string | null>(null);

  const [projectId, setProjectId] = useState(DEFAULT_ACCEPTANCE_PROJECT_ID);
  const [projectExamples, setProjectExamples] = useState<CliScreenExampleRecord[]>([]);
  const [projectLoaded, setProjectLoaded] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [projectDraft, setProjectDraft] = useState<ProjectExampleDraft>(EMPTY_PROJECT_DRAFT);

  const parsedGlobal = useMemo(() => tryParsePackSet(globalJson), [globalJson]);
  const parsedPack = useMemo(() => tryParseGlobalPack(packJson), [packJson]);
  const candidate = useMemo(
    () => buildGlobalCandidate(globalJson, selectedPackId, packJson),
    [globalJson, packJson, selectedPackId],
  );
  const candidateSnapshot = candidate ? JSON.stringify(candidate) : null;
  const canPublish = Boolean(
    candidateSnapshot
    && validatedSnapshot
    && candidateSnapshot === validatedSnapshot
    && validatedChecksum,
  );

  const filteredPacks = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const packs = parsedGlobal?.examples ?? [];
    if (!needle) return packs;
    return packs.filter((pack) => [
      pack.id,
      ...(pack.appliesWhen?.screenTypes ?? []),
      ...(pack.appliesWhen?.blockTypes ?? []),
      pack.fidelity ?? 'pattern',
    ].some((value) => value.toLowerCase().includes(needle)));
  }, [parsedGlobal, search]);

  useEffect(() => {
    if (adminLoaded && isAdmin) void refreshGlobal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminLoaded, isAdmin]);

  if (!adminLoaded) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }
  if (!isAdmin) return <Navigate to="/all-projects" replace />;

  async function refreshGlobal(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const [artifact, history] = await Promise.all([
        api.getGlobalArtifact(),
        api.listGlobalVersions(),
      ]);
      const content = normalizeArtifactContent(artifact.content);
      const raw = JSON.stringify(content, null, 2);
      setGlobalJson(raw);
      const first = content.examples[0];
      setSelectedPackId(first?.id ?? '');
      setPackJson(first ? JSON.stringify(first, null, 2) : '');
      setVersions(history);
      setValidatedSnapshot(null);
      setValidatedChecksum(null);
    } catch (refreshError) {
      setError(errorMessage(refreshError));
    } finally {
      setLoading(false);
    }
  }

  function selectPack(pack: GlobalExamplePack): void {
    setSelectedPackId(pack.id);
    setPackJson(JSON.stringify(pack, null, 2));
    setValidatedSnapshot(null);
    setValidatedChecksum(null);
    setStatus(null);
  }

  function updatePack(mutator: (pack: GlobalExamplePack) => GlobalExamplePack): void {
    const current = tryParseGlobalPack(packJson);
    if (!current) {
      setError('Fix the pack JSON before editing structured fields.');
      return;
    }
    setPackJson(JSON.stringify(mutator(current), null, 2));
    setValidatedSnapshot(null);
    setValidatedChecksum(null);
    setError(null);
  }

  async function validateGlobal(): Promise<void> {
    if (!candidate || !candidateSnapshot) {
      setError('The global pack JSON is not valid.');
      return;
    }
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const result = await api.validateGlobalArtifact(candidate);
      setValidatedSnapshot(candidateSnapshot);
      setValidatedChecksum(result.checksum);
      setStatus(`Schema valid · sha256:${result.checksum}`);
    } catch (validationError) {
      setValidatedSnapshot(null);
      setValidatedChecksum(null);
      setError(errorMessage(validationError));
    } finally {
      setBusy(false);
    }
  }

  async function publishGlobal(): Promise<void> {
    if (!candidate || !canPublish) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.publishGlobalArtifact(candidate);
      setStatus(`Published immutable registry version · sha256:${result.checksum}`);
      await refreshGlobal();
      setBusy(false);
    } catch (publishError) {
      setError(errorMessage(publishError));
      setBusy(false);
    }
  }

  async function rollback(version: number): Promise<void> {
    if (!window.confirm(`Move the active prompt registry pointer to version ${version}?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.rollbackGlobalVersion(version);
      setStatus(`Active version moved to v${version}.`);
      await refreshGlobal();
      setBusy(false);
    } catch (rollbackError) {
      setError(errorMessage(rollbackError));
      setBusy(false);
    }
  }

  async function refreshProjectExamples(): Promise<void> {
    const normalizedProjectId = projectId.trim();
    if (!normalizedProjectId) {
      setError('Select a project before loading examples.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.listProjectExamples(normalizedProjectId);
      setProjectExamples(result.examples);
      setProjectLoaded(true);
      setStatus(`Loaded ${result.examples.length} project example(s).`);
    } catch (projectError) {
      setError(errorMessage(projectError));
      setProjectLoaded(false);
    } finally {
      setBusy(false);
    }
  }

  function openProjectExample(example?: CliScreenExampleRecord): void {
    if (!example) {
      setProjectDraft(EMPTY_PROJECT_DRAFT);
    } else {
      setProjectDraft({
        id: example.id,
        name: example.name,
        screenTypes: example.screenTypes.join(', '),
        blockTypes: (example.blockTypes ?? []).join(', '),
        fidelity: example.fidelity,
        bodyJson: JSON.stringify({
          teaches: example.teaches,
          outputPattern: example.outputPattern,
          forbiddenCopyBehavior: example.forbiddenCopyBehavior,
        }, null, 2),
        enabled: example.enabled,
        persisted: true,
      });
    }
    setDrawerOpen(true);
    setError(null);
  }

  async function saveProjectExample(): Promise<void> {
    const input = buildProjectInput(projectDraft);
    if (!projectId.trim() || !projectDraft.id.trim() || !input) {
      setError('Project, example id, name, target screen types, and valid example JSON are required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.saveProjectExample(projectId.trim(), projectDraft.id.trim(), input);
      setStatus(`${result.created ? 'Created' : 'Updated'} ${result.example.id}.`);
      setDrawerOpen(false);
      await refreshProjectExamples();
    } catch (saveError) {
      setError(errorMessage(saveError));
      setBusy(false);
    }
  }

  async function deleteProjectExample(): Promise<void> {
    if (!projectDraft.persisted) return;
    if (!window.confirm(`Delete project example ${projectDraft.id}?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteProjectExample(projectId.trim(), projectDraft.id);
      setStatus(`Deleted ${projectDraft.id}.`);
      setDrawerOpen(false);
      await refreshProjectExamples();
    } catch (deleteError) {
      setError(errorMessage(deleteError));
      setBusy(false);
    }
  }

  return (
    <Box data-testid={PROMPT_SECTIONS_ADMIN.page} sx={{ p: 3, maxWidth: 1480, mx: 'auto' }}>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>Prompt sections</Typography>
            <Typography color="text.secondary">
              Control implementation examples delivered to the strategy tactician.
            </Typography>
          </Box>
          <Chip label="Admin access" color="secondary" variant="outlined" />
        </Stack>

        <Tabs value={tab} onChange={(_event, value) => setTab(value)}>
          <Tab data-testid={PROMPT_SECTIONS_ADMIN.globalTab} value="global" label="Global example packs" />
          <Tab data-testid={PROMPT_SECTIONS_ADMIN.projectTab} value="project" label="Project examples" />
          <Tab data-testid={PROMPT_SECTIONS_ADMIN.promotionTab} value="promotion" label="Subtype promotion" />
        </Tabs>

        {error && <Alert severity="error">{error}</Alert>}
        {status && <Alert data-testid={PROMPT_SECTIONS_ADMIN.statusMessage} severity="success">{status}</Alert>}

        {tab === 'global' ? (
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={700}>{BLOCK_SCREEN_EXAMPLE_PACKS_PATH}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Active v{versions?.pointer?.activeVersion ?? '—'} · latest v{versions?.pointer?.latestVersion ?? '—'} · {parsedGlobal?.examples.length ?? 0} packs
                </Typography>
              </Box>
              <Button variant="outlined" startIcon={<RefreshIcon />} disabled={busy} onClick={() => void refreshGlobal()}>
                Refresh
              </Button>
              <Button
                data-testid={PROMPT_SECTIONS_ADMIN.globalValidate}
                variant="outlined"
                startIcon={<VerifiedIcon />}
                disabled={busy || !candidate}
                onClick={() => void validateGlobal()}
              >
                Validate
              </Button>
              <Button
                data-testid={PROMPT_SECTIONS_ADMIN.globalPublish}
                variant="contained"
                startIcon={<PublishIcon />}
                disabled={busy || !canPublish}
                onClick={() => void publishGlobal()}
              >
                Publish version
              </Button>
            </Stack>

            {loading ? (
              <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '280px minmax(440px, 1fr) 280px' }, gap: 2 }}>
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Stack spacing={1.5} sx={{ p: 1.5 }}>
                    <Typography variant="h6">Example packs</Typography>
                    <StyledTextField value={search} onChange={setSearch} label="Search id or screen type" size="small" />
                  </Stack>
                  <Divider />
                  <List data-testid={PROMPT_SECTIONS_ADMIN.globalList} disablePadding sx={{ maxHeight: 610, overflow: 'auto' }}>
                    {filteredPacks.map((pack) => (
                      <ListItemButton
                        key={pack.id}
                        data-testid={PROMPT_SECTIONS_ADMIN.globalPack(pack.id)}
                        selected={selectedPackId === pack.id}
                        onClick={() => selectPack(pack)}
                      >
                        <ListItemText
                          primary={pack.id}
                          secondary={(pack.appliesWhen?.screenTypes ?? []).join(', ') || 'Any screen type'}
                          primaryTypographyProps={{ sx: { fontFamily: 'monospace', fontSize: 12, overflowWrap: 'anywhere' } }}
                        />
                        <Chip size="small" label={pack.fidelity ?? 'pattern'} />
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6">Edit global pack</Typography>
                      {validatedChecksum && <Chip color="success" size="small" label="Validated" />}
                    </Stack>
                    <StyledTextField
                      value={parsedPack?.id ?? ''}
                      onChange={(value) => updatePack((pack) => ({ ...pack, id: value }))}
                      label="Pack id"
                      fullWidth
                    />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 220px' }, gap: 1.5 }}>
                      <StyledTextField
                        value={(parsedPack?.appliesWhen?.screenTypes ?? []).join(', ')}
                        onChange={(value) => updatePack((pack) => ({
                          ...pack,
                          appliesWhen: { ...pack.appliesWhen, screenTypes: splitList(value) },
                        }))}
                        label="Screen types"
                        fullWidth
                      />
                      <StyledSelectDropdown
                        value={parsedPack?.fidelity ?? 'pattern'}
                        onChange={(value) => updatePack((pack) => ({
                          ...pack,
                          fidelity: value as CliScreenExampleFidelity,
                        }))}
                        options={FIDELITY_OPTIONS}
                        label="Fidelity"
                      />
                    </Box>
                    <StyledTextArea
                      data-testid={PROMPT_SECTIONS_ADMIN.globalJson}
                      value={packJson}
                      onChange={(value) => {
                        setPackJson(value);
                        setValidatedSnapshot(null);
                        setValidatedChecksum(null);
                      }}
                      label="Pack JSON"
                      rows={18}
                      resize="vertical"
                      error={Boolean(packJson && !parsedPack)}
                      errorMessage={packJson && !parsedPack ? 'Pack JSON must be a JSON object.' : undefined}
                    />
                    {canPublish && (
                      <Alert severity="success">
                        Schema valid. Publishing will create an immutable registry version.
                      </Alert>
                    )}
                  </Stack>
                </Paper>

                <Paper data-testid={PROMPT_SECTIONS_ADMIN.globalHistory} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1.5 }}>Version history</Typography>
                  <Stack divider={<Divider flexItem />}>
                    {(versions?.versions ?? []).map((version) => (
                      <VersionRow
                        key={version.version}
                        version={version}
                        active={versions?.pointer?.activeVersion === version.version}
                        busy={busy}
                        onRollback={rollback}
                      />
                    ))}
                  </Stack>
                </Paper>
              </Box>
            )}
          </Stack>
        ) : tab === 'project' ? (
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
              <Box sx={{ flex: 1, maxWidth: 520 }}>
                <StyledTextField
                  data-testid={PROMPT_SECTIONS_ADMIN.projectId}
                  value={projectId}
                  onChange={(value) => {
                    setProjectId(value);
                    setProjectLoaded(false);
                  }}
                  label="Project id"
                  helperText="Project scope is explicit; examples never leak across projects."
                  fullWidth
                />
              </Box>
              <Button
                data-testid={PROMPT_SECTIONS_ADMIN.projectRefresh}
                variant="outlined"
                startIcon={<RefreshIcon />}
                disabled={busy || !projectId.trim()}
                onClick={() => void refreshProjectExamples()}
              >
                Load project
              </Button>
              <Button
                data-testid={PROMPT_SECTIONS_ADMIN.projectNew}
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!projectLoaded || projectExamples.length >= 10}
                onClick={() => openProjectExample()}
              >
                New project example
              </Button>
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 340px' }, gap: 2 }}>
              <Paper variant="outlined" sx={{ overflow: 'auto' }}>
                <Table data-testid={PROMPT_SECTIONS_ADMIN.projectList}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Example</TableCell>
                      <TableCell>Targets</TableCell>
                      <TableCell>Fidelity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projectExamples.map((example) => (
                      <TableRow key={example.id} data-testid={PROMPT_SECTIONS_ADMIN.projectExample(example.id)}>
                        <TableCell><Typography fontWeight={600}>{example.name}</Typography><Typography variant="caption" fontFamily="monospace">{example.id}</Typography></TableCell>
                        <TableCell>{example.screenTypes.map((screenType) => <Chip key={screenType} size="small" label={screenType} sx={{ mr: 0.5, mb: 0.5 }} />)}</TableCell>
                        <TableCell>{example.fidelity}</TableCell>
                        <TableCell><Chip size="small" color={example.enabled ? 'success' : 'default'} label={example.enabled ? 'Enabled' : 'Disabled'} /></TableCell>
                        <TableCell align="right"><Button size="small" onClick={() => openProjectExample(example)}>Edit</Button></TableCell>
                      </TableRow>
                    ))}
                    {projectLoaded && projectExamples.length === 0 && (
                      <TableRow><TableCell colSpan={5}><Typography color="text.secondary">No project examples.</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
              <Alert severity="info" sx={{ alignSelf: 'start' }}>
                Enabled project examples are evaluated before global packs and pass the same target, fidelity, and proof filters. The runtime cap is 10 documents.
              </Alert>
            </Box>
          </Stack>
        ) : (
          <BlockSubtypePromotionPanel api={api} />
        )}
      </Stack>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => !busy && setDrawerOpen(false)}
        PaperProps={{
          'data-testid': PROMPT_SECTIONS_ADMIN.projectDrawer,
          sx: { width: { xs: '100%', sm: 600 }, p: 2.5 },
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" alignItems="flex-start">
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5">{projectDraft.persisted ? 'Edit' : 'New'} project example</Typography>
              <Typography variant="body2" color="text.secondary">{projectId} / screenExamples</Typography>
            </Box>
            <IconButton onClick={() => setDrawerOpen(false)} disabled={busy}><CloseIcon /></IconButton>
          </Stack>
          <StyledTextField
            data-testid={PROMPT_SECTIONS_ADMIN.projectExampleId}
            value={projectDraft.id}
            onChange={(value) => setProjectDraft((current) => ({ ...current, id: value }))}
            label="Example id"
            readOnly={projectDraft.persisted}
            helperText={projectDraft.persisted ? 'Document id is immutable.' : 'Use a stable path-safe id.'}
            fullWidth
          />
          <StyledTextField value={projectDraft.name} onChange={(value) => setProjectDraft((current) => ({ ...current, name: value }))} label="Name" fullWidth />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
            <StyledTextField value={projectDraft.screenTypes} onChange={(value) => setProjectDraft((current) => ({ ...current, screenTypes: value }))} label="Screen types" fullWidth />
            <StyledTextField value={projectDraft.blockTypes} onChange={(value) => setProjectDraft((current) => ({ ...current, blockTypes: value }))} label="Block types (optional)" fullWidth />
          </Box>
          <StyledSelectDropdown
            value={projectDraft.fidelity}
            onChange={(value) => setProjectDraft((current) => ({ ...current, fidelity: value as CliScreenExampleFidelity }))}
            options={FIDELITY_OPTIONS}
            label="Fidelity"
          />
          <StyledTextArea
            data-testid={PROMPT_SECTIONS_ADMIN.projectExampleJson}
            value={projectDraft.bodyJson}
            onChange={(value) => setProjectDraft((current) => ({ ...current, bodyJson: value }))}
            label="Example JSON"
            rows={14}
            error={Boolean(projectDraft.bodyJson && !tryParseProjectBody(projectDraft.bodyJson))}
            errorMessage={projectDraft.bodyJson && !tryParseProjectBody(projectDraft.bodyJson)
              ? 'Expected teaches[], outputPattern{}, and forbiddenCopyBehavior[].'
              : undefined}
          />
          <FormControlLabel
            control={<Switch checked={projectDraft.enabled} onChange={(_event, checked) => setProjectDraft((current) => ({ ...current, enabled: checked }))} />}
            label="Enabled in new prompt renders"
          />
          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button
              data-testid={PROMPT_SECTIONS_ADMIN.projectExampleDelete}
              color="error"
              startIcon={<DeleteOutlineIcon />}
              disabled={busy || !projectDraft.persisted}
              onClick={() => void deleteProjectExample()}
            >
              Delete
            </Button>
            <Stack direction="row" spacing={1}>
              <Button disabled={busy} onClick={() => setDrawerOpen(false)}>Cancel</Button>
              <Button
                data-testid={PROMPT_SECTIONS_ADMIN.projectExampleSave}
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={busy || !buildProjectInput(projectDraft) || !projectDraft.id.trim()}
                onClick={() => void saveProjectExample()}
              >
                Apply project example
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Drawer>
    </Box>
  );
}

function VersionRow({
  version,
  active,
  busy,
  onRollback,
}: {
  version: PromptRegistryVersionView;
  active: boolean;
  busy: boolean;
  onRollback: (version: number) => Promise<void>;
}): React.ReactElement {
  return (
    <Box sx={{ py: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography fontFamily="monospace" fontWeight={700}>v{version.version}</Typography>
        {active && <Chip size="small" color="success" label="ACTIVE" />}
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block">
        {version.author || version.source || 'unknown author'} · {formatRegistryDate(version.createdAt)}
      </Typography>
      {!active && (
        <Button
          data-testid={PROMPT_SECTIONS_ADMIN.globalRollback(version.version)}
          size="small"
          color="warning"
          startIcon={<RestoreIcon />}
          disabled={busy}
          onClick={() => void onRollback(version.version)}
          sx={{ mt: 0.75 }}
        >
          Rollback
        </Button>
      )}
    </Box>
  );
}

function normalizeArtifactContent(content: unknown): GlobalExamplePackSet {
  const parsed = typeof content === 'string' ? JSON.parse(content) : content;
  if (!isRecord(parsed) || typeof parsed.version !== 'string' || !Array.isArray(parsed.examples)) {
    throw new Error('The global example artifact does not match the expected pack set shape.');
  }
  return parsed as unknown as GlobalExamplePackSet;
}

function tryParsePackSet(raw: string): GlobalExamplePackSet | null {
  try {
    return normalizeArtifactContent(JSON.parse(raw));
  } catch {
    return null;
  }
}

function tryParseGlobalPack(raw: string): GlobalExamplePack | null {
  try {
    const value = JSON.parse(raw);
    if (!isRecord(value) || typeof value.id !== 'string' || !isRecord(value.appliesWhen)) return null;
    return value as unknown as GlobalExamplePack;
  } catch {
    return null;
  }
}

function buildGlobalCandidate(
  globalRaw: string,
  originalPackId: string,
  selectedRaw: string,
): GlobalExamplePackSet | null {
  const packSet = tryParsePackSet(globalRaw);
  const selected = tryParseGlobalPack(selectedRaw);
  if (!packSet || !selected) return null;
  const index = packSet.examples.findIndex((pack) => pack.id === originalPackId);
  if (index < 0) return null;
  const examples = [...packSet.examples];
  examples[index] = selected;
  return { ...packSet, examples };
}

function tryParseProjectBody(raw: string): Pick<CliScreenExampleInput, 'teaches' | 'outputPattern' | 'forbiddenCopyBehavior'> | null {
  try {
    const value = JSON.parse(raw);
    if (
      !isRecord(value)
      || !Array.isArray(value.teaches)
      || !isRecord(value.outputPattern)
      || !Array.isArray(value.forbiddenCopyBehavior)
    ) return null;
    return {
      teaches: value.teaches.filter((entry): entry is string => typeof entry === 'string'),
      outputPattern: value.outputPattern,
      forbiddenCopyBehavior: value.forbiddenCopyBehavior.filter((entry): entry is string => typeof entry === 'string'),
    };
  } catch {
    return null;
  }
}

function buildProjectInput(draft: ProjectExampleDraft): CliScreenExampleInput | null {
  const body = tryParseProjectBody(draft.bodyJson);
  const screenTypes = splitList(draft.screenTypes);
  const blockTypes = splitList(draft.blockTypes);
  if (!body || !draft.name.trim() || screenTypes.length === 0 || body.teaches.length === 0 || Object.keys(body.outputPattern).length === 0) {
    return null;
  }
  return {
    name: draft.name.trim(),
    screenTypes,
    ...(blockTypes.length ? { blockTypes } : {}),
    fidelity: draft.fidelity,
    teaches: body.teaches,
    outputPattern: body.outputPattern,
    forbiddenCopyBehavior: body.forbiddenCopyBehavior,
    enabled: draft.enabled,
  };
}

function splitList(value: string): string[] {
  return [...new Set(value.split(',').map((entry) => entry.trim()).filter(Boolean))];
}

function isRecord(value: unknown): value is Record<string, any> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Prompt sections operation failed.';
}

function formatRegistryDate(value: unknown): string {
  if (typeof value === 'string') return new Date(value).toLocaleString();
  if (isRecord(value)) {
    const seconds = typeof value.seconds === 'number' ? value.seconds : value._seconds;
    if (typeof seconds === 'number') return new Date(seconds * 1000).toLocaleString();
  }
  return 'unknown date';
}
